import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { listAllFarmerReportsForReviewer, listPostsForAdmin, listPublicFarmerReports, listPublicPosts } from '../lib/firestore';
import { extractFirstImageFromHtml } from '../lib/articleHtml';
import Seo from '../components/Seo';

function pickVisibleImageUrl(...candidates) {
  for (const candidate of candidates) {
    const value = String(candidate || '').trim();
    if (!value || value.startsWith('blob:')) continue;
    return value;
  }
  return '';
}

export default function PostsPage() {
  const { canReviewArticles } = useAuth();
  const [searchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [visibleCount, setVisibleCount] = useState(3);
  const listType = searchParams.get('type') === 'farmer' ? 'farmer' : 'all';

  useEffect(() => {
    const loadOperatorPosts = canReviewArticles ? listPostsForAdmin() : listPublicPosts();
    const loadFarmerReports = canReviewArticles ? listAllFarmerReportsForReviewer() : listPublicFarmerReports();

    Promise.all([
      loadOperatorPosts,
      loadFarmerReports,
    ]).then(([operatorPosts, farmerReports]) => {
      const merged = [
        ...operatorPosts.map((post) => ({
          ...post,
          entryType: 'operator',
          entryKey: `post-${post.id}`,
          href: `/posts/${post.slug}`,
          imageUrl: pickVisibleImageUrl(
            post.introImageUrl,
            post.coverImageUrl,
            extractFirstImageFromHtml(post.contentHtml || '')
          ),
          metaLabel: canReviewArticles
            ? `運営記事 (${post.approvalStatus || 'approved'} / ${post.visibility || 'public'})`
            : '運営記事',
          summary: post.body || '',
        })),
        ...farmerReports.map((report) => ({
          ...report,
          entryType: 'farmer-report',
          entryKey: `report-${report.farmerId}-${report.id}`,
          href: canReviewArticles
            ? `/admin/reports?editor=farmer&mode=edit&farmerId=${encodeURIComponent(report.farmerId)}&reportId=${encodeURIComponent(report.id)}`
            : (report.farmerSlug ? `/farmers/${report.farmerSlug}#monthly-report-${report.id}` : '/farmers'),
          imageUrl: pickVisibleImageUrl(
            report.introImageUrl,
            report.coverImageUrl,
            extractFirstImageFromHtml(report.contentHtml || '')
          ),
          metaLabel: canReviewArticles
            ? `${report.farmerName || '農家'} の近況報告 (${report.approvalStatus || 'approved'} / ${report.visibility || 'public'})`
            : (report.farmerName ? `${report.farmerName} の近況報告` : '農家近況報告'),
          summary: report.body || '',
        })),
      ].sort((a, b) => String(b.publishedDate || b.month || '').localeCompare(String(a.publishedDate || a.month || '')));

      setPosts(merged);
      setVisibleCount(3);
    });
  }, [canReviewArticles]);

  const filteredPosts = listType === 'farmer'
    ? posts.filter((post) => post.entryType === 'farmer-report')
    : posts;
  const visiblePosts = filteredPosts.slice(0, visibleCount);
  const hasMorePosts = visibleCount < filteredPosts.length;

  return (
    <>
      <Seo
        title={listType === 'farmer' ? '農家記事一覧' : '運営レポート'}
        description={listType === 'farmer'
          ? 'Drs.Farmに参加する農家さんの近況報告を一覧で読めます。'
          : '農家会員の取り組みと、医師・運営の視点を伝える記事アーカイブです。'}
        path={listType === 'farmer' ? '/posts?type=farmer' : '/posts'}
        noindex={canReviewArticles}
      />
      <h1>{listType === 'farmer' ? '農家記事一覧' : '運営レポート'}</h1>
      <p className="page-lead">
        {listType === 'farmer'
          ? '農家近況報告の一覧です。'
          : '農家会員の取り組みと、医師・運営の視点を伝える記事アーカイブです。'}
      </p>
      <div className="cards posts-archive-grid">
        {visiblePosts.map((post) => (
          <Link
            key={post.entryKey || post.id}
            className={`card posts-archive-card ${post.imageUrl ? 'has-image' : 'no-image'}`}
            to={post.href}
            style={post.imageUrl ? { backgroundImage: `url(${post.imageUrl})` } : undefined}
          >
            <div className="card-body posts-archive-card-body">
              <p className="meta-text">{post.metaLabel}</p>
              <h2>{post.title}</h2>
              {(post.publishedDate || post.month) ? <p className="meta-text">{post.publishedDate || post.month}</p> : null}
              {post.summary ? <p>{post.summary}</p> : null}
              {post.tags?.length ? <p>タグ: {post.tags.join(' / ')}</p> : null}
              {post.cropTags?.length ? <p>野菜タグ: {post.cropTags.join(' / ')}</p> : null}
              <span className="text-btn">{post.entryType === 'farmer-report' ? '近況報告を見る' : '続きを読む'}</span>
            </div>
          </Link>
        ))}
      </div>
      {hasMorePosts ? (
        <div className="posts-archive-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setVisibleCount((current) => Math.min(current + 3, filteredPosts.length))}
          >
            さらに読み込む
          </button>
        </div>
      ) : null}
    </>
  );
}
